import React from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Grid,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { FaSearch, FaCalendarAlt, FaFilePdf, FaUndo } from "react-icons/fa";

const TimeEntryFilters = ({
  filters,
  setFilters,
  users,
  projects,
  loading,
  handleExportPDF,
}) => {
  const handleResetFilters = () => {
    setFilters({
      name: "",
      projectId: "",
      approvalStatus: "",
      searchTerm: "",
      startDate: null,
      endDate: null,
    });
  };

  // Helper function to format date for input
  const formatDateForInput = (date) => {
    if (!date) return "";

    try {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (e) {
      return "";
    }
  };

  // Helper function to parse date from input
  const handleDateChange = (field, value) => {
    try {
      const date = value ? new Date(value) : null;
      setFilters({ ...filters, [field]: date });
    } catch (e) {
      setFilters({ ...filters, [field]: null });
    }
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (
      filters.searchTerm ||
      filters.name ||
      filters.projectId ||
      filters.approvalStatus ||
      filters.startDate ||
      filters.endDate
    );
  };

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12}>
        <div className="w-full">
          <div className="flex gap-2 mx-4">
            <div className="relative flex-1">
              <input
                type="text"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                placeholder="Search by user name, project name, scaffold ID, comments..."
                value={filters.searchTerm || ""}
                onChange={(e) =>
                  setFilters({ ...filters, searchTerm: e.target.value })
                }
                aria-label="Search time entries"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <FaSearch />
              </span>
            </div>

            <div>
              <button
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleResetFilters}
                disabled={!hasActiveFilters()}
                aria-label="Reset filters"
              >
                <FaUndo className="mr-1" />
                Reset
              </button>
            </div>
          </div>
        </div>
      </Grid>

      {/* Date Range Filters - Using styled HTML date inputs */}
      <Grid item xs={12}>
        <Typography
          variant="subtitle1"
          className="mb-2 font-medium flex items-center"
        >
          <FaCalendarAlt className="mr-2" />
          Date Range
        </Typography>

        <div className="flex flex-col md:flex-row gap-4 items-end">
          {/* Date Inputs Container */}
          <div className="flex flex-col sm:flex-row gap-4 w-full">
            {/* Start Date */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formatDateForInput(filters.startDate)}
                onChange={(e) => handleDateChange("startDate", e.target.value)}
                aria-label="Start date filter"
              />
            </div>

            {/* End Date */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formatDateForInput(filters.endDate)}
                onChange={(e) => handleDateChange("endDate", e.target.value)}
                aria-label="End date filter"
              />
            </div>
          </div>

          {/* PDF Button - Aligned properly with date inputs */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<FaFilePdf />}
            onClick={handleExportPDF}
            disabled={loading.timeEntries}
            className="w-full md:w-auto h-[42px] mt-6 md:mt-0" // Match height with inputs
            aria-label="Export to PDF"
          >
             PDF
          </Button>
        </div>
      </Grid>

      <Grid item xs={12} md={4}>
        <FormControl fullWidth variant="outlined">
          <InputLabel id="filterUser">Filter by User</InputLabel>
          <Select
            labelId="filterUser"
            value={filters.name || "all_users"}
            onChange={(e) => setFilters({ ...filters, name: e.target.value })}
            label="Filter by User"
            disabled={loading.users}
            aria-label="Filter by user"
          >
            <MenuItem value="all_users">All Users</MenuItem>
            {Array.isArray(users) && users.length > 0 ? (
              users.map((user) => (
                <MenuItem key={user._id} value={user.name}>
                  {user.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No users found</MenuItem>
            )}
          </Select>
          {loading.users && (
            <CircularProgress
              size={20}
              style={{
                position: "absolute",
                right: 30,
                top: "50%",
                marginTop: -10,
              }}
            />
          )}
        </FormControl>
      </Grid>

      <Grid item xs={12} md={4}>
        <FormControl fullWidth variant="outlined">
          <InputLabel id="filterProject">Filter by Project</InputLabel>
          <Select
            labelId="filterProject"
            value={filters.projectId || "all_projects"}
            onChange={(e) =>
              setFilters({ ...filters, projectId: e.target.value })
            }
            label="Filter by Project"
            disabled={loading.projects}
            aria-label="Filter by project"
          >
            <MenuItem value="all_projects">All Projects</MenuItem>
            {projects && projects.length > 0 ? (
              projects.map((project) => (
                <MenuItem key={project._id} value={project._id}>
                  {project.projectName}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No projects found</MenuItem>
            )}
          </Select>
          {loading.projects && (
            <CircularProgress
              size={20}
              style={{
                position: "absolute",
                right: 30,
                top: "50%",
                marginTop: -10,
              }}
            />
          )}
        </FormControl>
      </Grid>

      <Grid item xs={12} md={4}>
        <FormControl fullWidth variant="outlined">
          <InputLabel id="filterStatus">Filter by Status</InputLabel>
          <Select
            labelId="filterStatus"
            value={filters.approvalStatus || "all_statuses"}
            onChange={(e) =>
              setFilters({ ...filters, approvalStatus: e.target.value })
            }
            label="Filter by Status"
            aria-label="Filter by approval status"
          >
            <MenuItem value="all_statuses">All Statuses</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default TimeEntryFilters;
